import React from 'react';

import { LandingChevronRight } from 'assets/svg/landing/landingIcons';
import StudioCardIcon from 'components/atoms/Landing/StudioCardIcon';
import { rgmStudioCardTestId, rgmStudioViewMoreTestId } from 'constants/landingTestIds';
import { StudioGroup } from 'types/landing';

interface Props {
    group: StudioGroup;
    isSelected: boolean;
    /** True when some other card owns the open panel. */
    isMuted: boolean;
    onViewMore: (group: StudioGroup) => void;
}

/**
 * Shared card chrome — the detail panel is the same shell at full grid width. The border colour
 * is the consumer's to set: selection is nothing but a swap from the grey to `landingAccent`.
 */
export const STUDIO_CARD_SHELL = 'landing-card-glow relative overflow-clip rounded-[12px] border bg-white';

/**
 * One navigation group as a card.
 *
 * Muted cards (another card owns the open panel) stay clickable so a pointer user can switch the
 * selection in one click — hovering one lifts it back to full opacity as its affordance. They drop
 * out of the tab order (`inert` is not universally supported, so this uses `tabIndex={-1}` plus
 * `aria-hidden`) — otherwise they become invisible focus traps while a panel is open.
 */
function StudioCard({ group, isSelected, isMuted, onViewMore }: Props) {
    const isInactive = isMuted || group.isDisabled;

    const handleActivate = () => {
        if (group.isDisabled) return;
        onViewMore(group);
    };

    return (
        <div
            data-testid={rgmStudioCardTestId(group.key)}
            aria-hidden={isMuted || undefined}
            role="button"
            tabIndex={isInactive ? -1 : 0}
            aria-disabled={group.isDisabled || undefined}
            onClick={handleActivate}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleActivate();
                }
            }}
            className={`${STUDIO_CARD_SHELL} flex min-w-px flex-[1_0_0] flex-col px-4 py-5 transition-[opacity,border-color] duration-200 ${
                isSelected ? 'border-landingAccent' : 'border-filterV2Border'
            } ${isInactive ? 'opacity-40' : ''} ${
                isMuted && !group.isDisabled ? 'hover:opacity-100' : ''
            } ${group.isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
            <div className="relative flex flex-col gap-4">
                <div className="flex flex-col gap-3">
                    <div className="flex w-full items-start justify-between">
                        <StudioCardIcon icon={group.icon} groupKey={group.key} />
                    </div>
                    <div className="flex w-full flex-col justify-center gap-2 break-words">
                        <p className="font-lg font-medium leading-[1.2] text-blackTitle">{group.label}</p>
                        <p className="font-sm font-normal leading-[normal] text-filterV2IconMuted">
                            {group.description}
                        </p>
                    </div>
                </div>

                <span
                    data-testid={rgmStudioViewMoreTestId(group.key)}
                    className="flex w-fit items-center gap-1.5 rounded py-2.5 font-sm font-normal leading-6 text-filterV2TextPrimary"
                >
                    Explore
                    <LandingChevronRight className="size-3" />
                </span>
            </div>
        </div>
    );
}

export default StudioCard;
